import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from "../../../../components/UI/Button";
import { Input } from "../../../../components/UI/Input";
import { Title } from "../../../../components/UI/Title";
import { Layout } from "../../../../containers/Layout/Layout";
import { Card, Table, Text, Loading, Paginate } from "../../../../components/UI";
import { userService } from '../../../../services';
import CONFIG from '../../../../../config';

interface IFamilySearchForm {
    searchTerm: string;
}

const pageDefault: number = 1;

export const AdminFamilyPage: FC = () => {
    const { register, handleSubmit, getValues } = useForm<IFamilySearchForm>();
    const [submited, setSubmited] = useState<boolean>(false);
    const [searchResults, setSearchResults] = useState<any>({});
    const [hasSearched, setHasSearched] = useState<boolean>(false);
    const [pageCurrent, setPageCurrent] = useState<number>(pageDefault);

    const getSearchTerm = (): string => getValues('searchTerm');

    const setUsersSearch = async (filter: string = '', page: number = 1) => {
        setSubmited(true);
        try {
            const usersData = await userService.getUsers(filter, 0, page, 25);
            if (usersData.status === 'Ok' && usersData.data.patients) {
                // Filtrar usuarios desactivados (status_data_id === 2)
                const activeUsers = usersData.data.patients.filter((user: any) => user.status_data_id !== 2);
                setSearchResults({
                    ...usersData.data,
                    patients: activeUsers,
                    count: activeUsers.length
                });
            } else {
                setSearchResults({});
            }
        } catch (error) {
            console.error('Error searching:', error);
            setSearchResults({});
        } finally {
            setSubmited(false);
            setHasSearched(true);
        }
    };

    const onSearchSubmit = async (data: IFamilySearchForm) => {
        setPageCurrent(pageDefault);
        setHasSearched(false);
        await setUsersSearch(data.searchTerm, pageDefault);
    };

    const onSearchPageCurrent = (pageCurrent: number) => {
        setPageCurrent(pageCurrent);
        setUsersSearch(getSearchTerm(), pageCurrent);
    };

    const tableColumns = [
        {
            accessor: 'document_number',
            Header: 'RUN o Pasaporte',
            width: '20%'
        },
        {
            accessor: 'first_name',
            Header: 'Nombres',
            width: '20%'
        },
        {
            accessor: 'last_name',
            Header: 'Primer apellido',
            width: '20%'
        },
        {
            accessor: 'second_name',
            Header: 'Segundo apellido',
            width: '20%'
        },
        {
            accessor: 'action',
            Header: 'Acción',
            width: '20%',
            Cell: (params: any) => {
                const { original } = params.row;
                return (
                    <Button 
                        component="link" 
                        to={`/admin/familia/${original.id}`}
                        size="md"
                    >
                        Gestionar Grupo Familiar
                    </Button>
                );
            }
        }
    ];

    return (
        <Layout role="ADMIN_ROLE" menu={true}>
            <Title border>Administrador de Grupos Familiares</Title>
            <Card className="mt-50 py-20 px-70">
                <Text size="16" weight="medium" className="mb-10">Buscar por RUN, Pasaporte o Nombre para comenzar</Text>
                <form noValidate autoComplete="off" onSubmit={handleSubmit(onSearchSubmit)}>
                    <div className="d-flex">
                        <div className="col pl-0" style={{ width: '100%' }}>
                            <Input
                                {...register('searchTerm')}
                                placeholder="Escribe aquí el usuario que buscas"
                                version="2"
                                icon="search"
                                fullWidth
                            />
                        </div>
                        <div style={{ flex: '0 0 85px', maxWidth: '85px' }}>
                            <Button
                                type="submit"
                                size="md"
                                disabled={submited}
                            >BUSCAR</Button>
                        </div>
                    </div>
                </form>

                <div className="position-relative mt-30" style={{ minHeight: 150 }}>
                    {searchResults && searchResults.patients && searchResults.patients.length > 0 ? (
                        <>
                            <Table
                                columns={tableColumns}
                                data={searchResults.patients}
                            />
                            <div className="text-center mt-20">
                                <Paginate 
                                    current={pageCurrent} 
                                    total={searchResults.count} 
                                    onPageChange={(current) => onSearchPageCurrent(current)}
                                />
                            </div>
                        </>
                    ) : hasSearched ? (
                        <div className="text-center py-50">
                            <Text size="16" weight="medium">No se ha encontrado al usuario</Text>
                            <div className="mt-20">
                                <Button 
                                    component="link" 
                                    to={CONFIG.ROUTES.ADMIN.USERS.CREATE.PATIENT}
                                    size="md"
                                >
                                    REGISTRAR USUARIO
                                </Button>
                            </div>
                        </div>
                    ) : null}
                    {submited && <Loading overlay />}
                </div>
            </Card>
        </Layout>
    );
};
